import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Setting Up React with Create React App
//  Chapter 1 · Intro
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "gold";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `# JavaScript template (default)
npx create-react-app my-app

# TypeScript template
npx create-react-app my-app --template typescript

cd my-app
npm start
# App runs at http://localhost:3000`;

const CODE_2 = `my-app/
  ├── public/
  │    ├── index.html        # Template HTML — DO NOT add scripts here
  │    ├── manifest.json     # PWA manifest
  │    └── favicon.ico
  ├── src/
  │    ├── App.js             # Root component
  │    ├── App.test.js        # Root component test
  │    ├── App.css
  │    ├── index.js           # Entry point — ReactDOM.createRoot
  │    ├── index.css
  │    └── reportWebVitals.js # Performance measuring (optional)
  ├── .gitignore
  └── package.json`;

const CODE_3 = `npm start          # Dev server at localhost:3000 with live reload
npm test           # Run Jest tests in watch mode
npm run build      # Production build → /build folder
npm run eject      # IRREVERSIBLE — exposes all Webpack config`;

const CODE_4 = `Feature          CRA               Vite
───────────────────────────────────────────────
Dev server start  ~10-30s            <1s
HMR speed         Seconds            Milliseconds
Build tool        Webpack            Rollup
Config            Hidden (eject)     vite.config.js
Bundle size       Larger             Smaller
Active maint.     Declining          Active
Recommended       Legacy / Learning  New projects`;


/* ── Styles ── */
const styles = {
    page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
    header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
    badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
    backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
    hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
    title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
    desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
    content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
    section: { marginBottom: "36px" },
    sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
    body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
    pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
    qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
    qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
    qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
    footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
    credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function SettingUpReactWithCRA() {
    const [copied, setCopied] = useState(null);

    function copyCode(codeStr, idx) {
        navigator.clipboard?.writeText(codeStr).then(() => {
            setCopied(idx);
            setTimeout(() => setCopied(null), 2000);
        });
    }

    return (
        <div style={styles.page}>

            {/* Header */}
            <header style={styles.header}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
                    <span style={styles.badge}>Chapter 1 · Intro</span>
                </div>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
            </header>

            {/* Hero */}
            <div style={styles.hero}>
                <h1 style={styles.title}>Setting Up React with Create React App</h1>
                <p style={styles.desc}>Use the classic Create React App (CRA) tool to scaffold and understand a React project.</p>
            </div>

            {/* Content */}
            <div style={styles.content}>
                <div style={styles.section}>
                    <h2 style={styles.sh}>What is Create React App?</h2>
                    <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Create React App (CRA) is the official tool from the React team for generating a fully configured React project. It abstracts away Webpack, Babel, ESLint, and Jest config so you can focus on writing React code immediately. While Vite has overtaken CRA in speed, CRA is still widely used in many teams and tutorials — making it essential to understand." }} />
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sh}>Creating a CRA Project</h2>
                    <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Use npx (bundled with npm 5.2+) to run CRA without a global install. CRA installs ~300MB of dependencies and takes 1–3 minutes." }} />
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => copyCode(CODE_1, 0)}
                            style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(97,218,251,0.15)',
                                border: '1px solid rgba(97,218,251,0.3)',
                                color: PRIMARY, borderRadius: '6px',
                                padding: '4px 10px', cursor: 'pointer',
                                fontSize: '0.72rem', fontWeight: 700,
                            }}
                        >
                            {copied === 0 ? '✓ Copied' : 'Copy'}
                        </button>
                        <pre style={styles.pre}>{String(CODE_1)}</pre>
                    </div>
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sh}>CRA Project Structure</h2>
                    <p style={styles.body} dangerouslySetInnerHTML={{ __html: "CRA gives a slightly more opinionated structure than Vite. Key files to know immediately:" }} />
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => copyCode(CODE_2, 1)}
                            style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(97,218,251,0.15)',
                                border: '1px solid rgba(97,218,251,0.3)',
                                color: PRIMARY, borderRadius: '6px',
                                padding: '4px 10px', cursor: 'pointer',
                                fontSize: '0.72rem', fontWeight: 700,
                            }}
                        >
                            {copied === 1 ? '✓ Copied' : 'Copy'}
                        </button>
                        <pre style={styles.pre}>{String(CODE_2)}</pre>
                    </div>
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sh}>CRA Available Scripts</h2>
                    <p style={styles.body} dangerouslySetInnerHTML={{ __html: "CRA comes with four scripts. It uses react-scripts which wraps Webpack under the hood." }} />
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => copyCode(CODE_3, 2)}
                            style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(97,218,251,0.15)',
                                border: '1px solid rgba(97,218,251,0.3)',
                                color: PRIMARY, borderRadius: '6px',
                                padding: '4px 10px', cursor: 'pointer',
                                fontSize: '0.72rem', fontWeight: 700,
                            }}
                        >
                            {copied === 2 ? '✓ Copied' : 'Copy'}
                        </button>
                        <pre style={styles.pre}>{String(CODE_3)}</pre>
                    </div>
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sh}>CRA vs Vite Comparison</h2>
                    <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Choose the right tool for your project:" }} />
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => copyCode(CODE_4, 3)}
                            style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(97,218,251,0.15)',
                                border: '1px solid rgba(97,218,251,0.3)',
                                color: PRIMARY, borderRadius: '6px',
                                padding: '4px 10px', cursor: 'pointer',
                                fontSize: '0.72rem', fontWeight: 700,
                            }}
                        >
                            {copied === 3 ? '✓ Copied' : 'Copy'}
                        </button>
                        <pre style={styles.pre}>{String(CODE_4)}</pre>
                    </div>
                </div>

                <div style={styles.section}>
                    <h2 style={styles.sh}>Practice Questions</h2>
                    <div style={styles.qBox}>
                        <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
                        <div style={styles.qItem}>✦ 1. What is the difference between npm start and npm run build?</div>
                        <div style={styles.qItem}>✦ 2. Why should you almost never run npm run eject?</div>
                        <div style={styles.qItem}>✦ 3. What does CRA use under the hood for bundling?</div>
                        <div style={styles.qItem}>✦ 4. How do you add TypeScript support to a CRA project?</div>
                        <div style={styles.qItem}>✦ 5. When would you choose CRA over Vite today?</div>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
                </div>
            </div>

        </div>
    );
}
